import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField, SelectField, Options, Cell } from '../components/Field.jsx';
import { Loader, ErrorAlert, EmptyRow } from '../components/States.jsx';
import useForm from '../hooks/useForm.js';
import useRowEditor from '../hooks/useRowEditor.js';
import {
  useListStockInQuery,
  useCreateStockInMutation,
  useUpdateStockInMutation,
  useDeleteStockInMutation,
  useGetStockAvailabilityQuery,
} from '../store/apiSlice.js';
import { stockInSchema, stockInRowSchema, stockInEditSchema } from '../validation/schemas.js';
import { validateRowSet, remapRowErrors, rowError } from '../validation/rules.js';
import { useToast } from '../context/ToastContext.jsx';
import { useReferenceData } from '../store/useReferenceData.js';
import InventoryTable from '../components/InventoryTable.jsx';
import { fmtDate, today } from '../utils/format.js';
import { downloadCsv } from '../utils/csv.js';

const blankRow = () => ({ product: '', texture: '', length: '', color: '', qty: '' });
const skuKey = (row) => [row.product, row.texture, row.length, row.color].map((v) => String(v ?? '').trim()).join('||');

export default function StockInPage() {
  const showToast = useToast();
  const { products, textures, lengths, colors, vendors } = useReferenceData();
  const { data: history, isLoading: loading, error: listError, refetch: reload } = useListStockInQuery();
  const error = listError?.message ?? null;

  /**
   * Live stock per SKU (received − sold). Every Stock In and Stock Out save
   * invalidates this, so it is current the moment an invoice is saved.
   * Receipt rows below keep their own "stock after receipt" snapshot — that is
   * history and deliberately does not change when something is sold.
   */
  const { data: inventory, isSuccess: stockLoaded } = useGetStockAvailabilityQuery();
  const onShelf = useMemo(() => {
    const map = new Map();
    for (const row of inventory || []) map.set(skuKey(row), row);
    return map;
  }, [inventory]);
  const currentOf = (row) => (stockLoaded ? onShelf.get(skuKey(row))?.available ?? 0 : null);

  const [createStockIn] = useCreateStockInMutation();
  const [updateStockIn] = useUpdateStockInMutation();
  const [deleteStockIn] = useDeleteStockInMutation();

  const form = useForm({ vendor: '', date: today() }, stockInSchema);
  const [rowErrors, setRowErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const { rows, addRow, removeRow, patchRow, resetRows } = useRowEditor(blankRow);

  /**
   * Receipts are saved in batches but corrected one at a time — each row of a
   * batch is its own record, with its own place in the stock ledger.
   */
  const [editing, setEditing] = useState(null);
  const editForm = useForm(
    { vendor: '', date: today(), product: '', texture: '', length: '', color: '', qty: '' },
    stockInEditSchema
  );

  const startEdit = (entry) => {
    setEditing(entry);
    editForm.fill({
      vendor: entry.vendor ?? '',
      date: entry.date,
      product: entry.product,
      texture: entry.texture,
      length: entry.length,
      color: entry.color,
      qty: String(entry.qty),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editForm.validate()) return;

    setBusy(true);
    try {
      const payload = { ...editForm.values, qty: Number(editForm.values.qty) };
      const { repairedRows } = await updateStockIn({ id: editing.id, payload }).unwrap();
      // Correcting an old receipt moves the running balance on everything after it.
      showToast(
        repairedRows
          ? `Entry corrected — ${repairedRows} later record(s) recalculated`
          : 'Entry corrected'
      );
      setEditing(null);
    } catch (apiError) {
      editForm.applyServerErrors(apiError.details);
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Clear a cell's error the moment the user edits that cell. */
  const editCell = (row, field, value) => {
    patchRow(row._key, { [field]: value });
    setRowErrors((current) => {
      const index = rows.indexOf(row);
      const key = `rows.${index}.${field}`;
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const err = (index, field) => rowError(rowErrors, 'rows', index, field);

  const save = async () => {
    const headerOk = form.validate();
    const { errors, payload, keep } = validateRowSet(rows, stockInRowSchema, { key: 'rows' });

    if (!payload.length) {
      setRowErrors({});
      showToast('Fill at least one row with a product and quantity', 'error');
      return;
    }
    setRowErrors(errors);
    if (!headerOk || Object.keys(errors).length) {
      showToast('Please fix the highlighted fields', 'error');
      return;
    }

    setBusy(true);
    try {
      const body = payload.map(({ _key, ...row }) => ({ ...row, qty: Number(row.qty) }));
      const created = await createStockIn({ ...form.values, rows: body }).unwrap();
      showToast(
        form.values.vendor
          ? `${created.length} row(s) received from ${form.values.vendor}`
          : `${created.length} row(s) received`
      );
      resetRows();
      setRowErrors({});
    } catch (apiError) {
      // The server keys row problems by payload index; translate to display rows.
      const details = apiError.details || {};
      setRowErrors(remapRowErrors(details, 'rows', keep));
      form.applyServerErrors(details);
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Remove this receipt of ${entry.qty} × ${entry.product}?`)) return;
    setBusy(true);
    try {
      await deleteStockIn(entry.id).unwrap();
      showToast('Stock in entry removed');
    } catch (apiError) {
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    downloadCsv('stock-in', [
      { label: 'Date', value: (entry) => entry.date },
      { label: 'Vendor', value: (entry) => entry.vendor ?? '' },
      { label: 'Product', value: (entry) => entry.product },
      { label: 'Texture', value: (entry) => entry.texture },
      { label: 'Length', value: (entry) => entry.length },
      { label: 'Color', value: (entry) => entry.color },
      { label: 'Previous Stock', value: (entry) => entry.previousStock ?? '' },
      { label: 'Received', value: (entry) => entry.qty },
      { label: 'Stock After Receipt', value: (entry) => entry.totalStock ?? '' },
      { label: 'Current Stock', value: (entry) => currentOf(entry) ?? '' },
      { label: 'Created At', value: (entry) => entry.createdAt || '' },
      { label: 'Last Updated', value: (entry) => entry.updatedAt || '' },
    ], history || []);
  };

  return (
    <>
      <PageHeader
        eyebrow="Stock"
        title="Stock In Management"
        subtitle="Select your vendor once, then keep adding product rows for that delivery. Previous and total stock are calculated for you."
      />

      <div className="selector-bar">
        <SelectField label="📦 Receiving From (Vendor, optional)" {...form.fieldProps('vendor')}>
          <Options items={vendors} placeholder="No vendor" />
        </SelectField>
        <div className="field" style={{ maxWidth: 190 }}>
          <label htmlFor="si-date">
            Date<span className="req" aria-hidden="true"> *</span>
          </label>
          <input
            id="si-date"
            type="date"
            max={today()}
            className={form.fieldProps('date').error ? 'invalid' : undefined}
            value={form.values.date}
            onChange={form.fieldProps('date').onChange}
            onBlur={form.fieldProps('date').onBlur}
          />
          {form.fieldProps('date').error && <div className="field-error">{form.fieldProps('date').error}</div>}
        </div>
        <div style={{ alignSelf: 'flex-end', color: 'var(--muted)', fontSize: '11.5px', paddingBottom: 2 }}>
          Pick a vendor (optional) — then add as many product rows as you need below.
        </div>
      </div>

      {editing && (
        <Card title="Correct Entry" tag="Stock In">
          <div className="editing-bar">
            <span>
              Correcting the receipt of {editing.qty} × {editing.product}{editing.vendor ? ` from ${editing.vendor}` : ''}. Every later record for
              this item will be recalculated.
            </span>
            <button type="button" className="action-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>

          <form onSubmit={saveEdit} noValidate>
            <div className="form-grid">
              <SelectField label="Vendor" {...editForm.fieldProps('vendor')}>
                <Options items={vendors} placeholder="No vendor" />
              </SelectField>
              <TextField type="date" label="Date" max={today()} {...editForm.fieldProps('date')} />
              <SelectField label="Product" {...editForm.fieldProps('product')}>
                <option value="">Select</option>
                <Options items={products} />
              </SelectField>
              <SelectField label="Texture" {...editForm.fieldProps('texture')}>
                <option value="">Select</option>
                <Options items={textures} />
              </SelectField>
              <SelectField label="Length" {...editForm.fieldProps('length')}>
                <option value="">Select</option>
                <Options items={lengths} />
              </SelectField>
              <SelectField label="Color" {...editForm.fieldProps('color')}>
                <option value="">Select</option>
                <Options items={colors} />
              </SelectField>
              <TextField type="number" label="Quantity" min="1" {...editForm.fieldProps('qty')} />
            </div>
            <div className="btn-row">
              <button className="btn primary" type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Update Entry'}
              </button>
            </div>
          </form>
        </Card>
      )}

      <Card title="Stock Entry Rows" tag="Add Items Received">
        <div style={{ overflowX: 'auto' }}>
          <table className="entry-table">
            <thead>
              <tr>
                <th>Product Name *</th>
                <th>Texture *</th>
                <th>Length *</th>
                <th>Color *</th>
                <th>Qty *</th>
                <th style={{ width: 40 }} aria-label="Remove row" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row._key}>
                  {['product', 'texture', 'length', 'color'].map((field) => (
                    <Cell key={field} error={err(index, field)}>
                      <select
                        value={row[field]}
                        onChange={(e) => editCell(row, field, e.target.value)}
                        className={err(index, field) ? 'invalid' : undefined}
                        aria-label={field}
                      >
                        <Options
                          items={{ product: products, texture: textures, length: lengths, color: colors }[field]}
                          placeholder={`Select ${field}`}
                        />
                      </select>
                    </Cell>
                  ))}
                  <Cell error={err(index, 'qty')}>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="0"
                      value={row.qty}
                      onChange={(e) => editCell(row, 'qty', e.target.value)}
                      className={err(index, 'qty') ? 'invalid' : undefined}
                      aria-label="Quantity"
                    />
                    {stockLoaded && row.product && row.texture && row.length && row.color && (
                      <div style={{ color: 'var(--muted)', fontSize: '10.5px', marginTop: 2, whiteSpace: 'nowrap' }}>
                        In stock now: {currentOf(row)}
                      </div>
                    )}
                  </Cell>
                  <td>
                    <button
                      type="button"
                      className="row-remove"
                      onClick={() => removeRow(row._key)}
                      disabled={rows.length === 1}
                      aria-label="Remove row"
                      title={rows.length === 1 ? 'Keep at least one row' : 'Remove row'}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="btn-row">
          <button type="button" className="btn ghost" onClick={addRow}>
            + Add Row
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save All'}
          </button>
        </div>
      </Card>

      <Card title="Current Inventory" tag="Received − Sold">
        <InventoryTable defaultView="all" maxHeight={420} />
      </Card>

      <Card title="Stock In History" tag="All Entries">
        <div className="table-toolbar">
          <button type="button" className="btn ghost" onClick={exportCsv} disabled={!history?.length}>
            Export CSV
          </button>
        </div>
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Product</th>
                  <th>Texture</th>
                  <th>Length</th>
                  <th>Color</th>
                  <th>Previous Stock</th>
                  <th>Received</th>
                  <th title="Stock level right after this delivery arrived">Stock After Receipt</th>
                  <th title="On the shelf now: all received minus all sold">Current Stock</th>
                  <th style={{ width: 80 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!history?.length ? (
                  <EmptyRow colSpan={11}>Record your first delivery above</EmptyRow>
                ) : (
                  history.map((entry) => (
                    <tr key={entry.id}>
                      <td className="nowrap">{fmtDate(entry.date)}</td>
                      <td>{entry.vendor || '—'}</td>
                      <td>{entry.product}</td>
                      <td>{entry.texture}</td>
                      <td>{entry.length}</td>
                      <td>{entry.color}</td>
                      <td className="num">{entry.previousStock ?? '—'}</td>
                      <td className="num">{entry.qty}</td>
                      <td className="num">{entry.totalStock ?? '—'}</td>
                      <td className="num"><strong>{currentOf(entry) ?? '—'}</strong></td>
                      <td>
                        <div className="row-actions">
                          <Link className="action-btn" to={`/stock-in/invoice/${entry.id}`}>
                            Invoice
                          </Link>
                          <button type="button" className="action-btn" onClick={() => startEdit(entry)} disabled={busy}>
                            Edit
                          </button>
                          <button type="button" className="action-btn delete" onClick={() => handleDelete(entry)} disabled={busy}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}